import type { TFunction } from 'i18next'
import {
  FRAME_COLORS,
  MATERIAL_TYPES,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { getSizeName } from 'services/SizesManager/SizesManagerHelper'
import { getFrameColorName } from 'utils/colorUtils'
import type { CartItem, TilesCartItem } from 'stores/cartStore/CartItem'

function getDistinctFrameCount(item: TilesCartItem): number {
  const regularProducts = item.metadata.pricingProducts.filter(
    (product) => product.pricingProduct.tileSize !== TILE_SIZES.RECTANGLE_25X8
  )
  return new Set(
    regularProducts.map(
      ({ pricingProduct }) =>
        `${pricingProduct.materialType ?? ''}-${
          pricingProduct.frameColor ?? ''
        }`
    )
  ).size
}

function getTilesCountLabel(count: number, t: TFunction): string {
  return t(
    count === 1
      ? 'order.cart_summary.tiles_one'
      : 'order.cart_summary.tiles_other',
    { count }
  )
}

function getWallSignsLabel(count: number, t: TFunction): string {
  return t(
    count === 1
      ? 'order.cart_summary.wall_signs_one'
      : 'order.cart_summary.wall_signs_other',
    { count }
  )
}

function getFrameBreakdownSizeLabel(
  item: TilesCartItem,
  t: TFunction,
  pricingCountry: string,
  isSingleFrame: boolean
): string {
  if (!isSingleFrame) return t('order.cart_summary.mixed_frames')
  const regularSizes = Object.keys(item.metadata.tileSizes)
  if (regularSizes.length > 1) return t('order.cart_summary.mixed_sizes_lower')
  return getSizeName({ size: regularSizes[0], t, pricingCountry })
}

function getFrameBreakdownClassicTitle(
  item: TilesCartItem,
  t: TFunction,
  pricingCountry: string
): string {
  const { tilesCount, centerpiecesCount } = item.metadata
  const regularCount = tilesCount - centerpiecesCount

  if (regularCount === 0) {
    return t('order.cart_summary.with_size', {
      item: getWallSignsLabel(centerpiecesCount, t),
      size: getSizeName({ size: TILE_SIZES.RECTANGLE_25X8, t, pricingCountry }),
    })
  }

  const isSingleFrame = getDistinctFrameCount(item) === 1
  const tilesItem = getTilesItemLabel(item, t, regularCount, isSingleFrame)

  if (centerpiecesCount === 0) {
    return t('order.cart_summary.with_size', {
      item: tilesItem,
      size: getFrameBreakdownSizeLabel(item, t, pricingCountry, isSingleFrame),
    })
  }

  return t('order.cart_summary.combined_no_size', {
    tiles: tilesItem,
    signs: getWallSignsLabel(centerpiecesCount, t),
  })
}

function getTilesItemLabel(
  item: TilesCartItem,
  t: TFunction,
  count: number,
  isSingleFrame: boolean
): string {
  if (!isSingleFrame) return getTilesCountLabel(count, t)
  const { frameColor, materialType } = item.metadata.pricingProducts.find(
    (p) => p.pricingProduct.tileSize !== TILE_SIZES.RECTANGLE_25X8
  )!.pricingProduct
  const frame = getFrameColorName(
    frameColor ?? FRAME_COLORS.NONE,
    materialType === MATERIAL_TYPES.WIDE_FRAME,
    materialType,
    t
  )
  if (materialType === MATERIAL_TYPES.CANVAS) {
    return t('order.cart_summary.canvas_tiles', { count, frame })
  }
  return t(
    count === 1
      ? 'order.cart_summary.frame_tiles_one'
      : 'order.cart_summary.frame_tiles_many',
    { count, frame }
  )
}

export function generateTitle(
  item: CartItem,
  t: TFunction,
  pricingCountry: string,
  isPriceTierListEnabled: boolean
): string {
  switch (item.productType) {
    case PRODUCT_TYPES.CLASSIC:
      return getFrameBreakdownClassicTitle(item, t, pricingCountry)
    case PRODUCT_TYPES.PHOTO_BOOK: {
      const size = getSizeName({ size: item.metadata.size, t, pricingCountry })
      return isPriceTierListEnabled
        ? t('order.cart_summary.book_with_pages', {
            size,
            pageCount: item.metadata.photosCount,
          })
        : t('order.cart_summary.photo_book', { size })
    }
    default:
      return ''
  }
}
